import React, {useEffect, useMemo, useState} from 'react';
import {ActivityIndicator, Keyboard, Platform, View} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';

import KeyboardFocusable from '/component/base/ExtKeyboard/KeyboardFocusable';
import Text from '/component/base/Text';
import {ManageMySiteTestIDs} from '/constant/TestIDs';
import {tt} from '/provider/TranslationProvider/TranslationProvider.util';

import {t} from '../ManageMySite.constants';

import styles from './StickyActionFooter.style';
import type {StickyActionFooterProps} from './StickyActionFooter.types';

export const useKeyboardVisible = () => {
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

  useEffect(() => {
    const showEvent =
      Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideEvent =
      Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';

    const showSub = Keyboard.addListener(showEvent, () =>
      setIsKeyboardVisible(true),
    );
    const hideSub = Keyboard.addListener(hideEvent, () =>
      setIsKeyboardVisible(false),
    );

    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, []);

  return isKeyboardVisible;
};

export const StickyActionFooter: React.FC<StickyActionFooterProps> = ({
  onSave,
  onCancel,
  isSaving = false,
  showSave = true,
  showCancel = false,
  disableSave = false,
  testID = ManageMySiteTestIDs.footer.container,
  saveTestID = ManageMySiteTestIDs.footer.save,
  cancelTestID = ManageMySiteTestIDs.footer.cancel,
  saveLabel,
  cancelLabel,
  footerA11yLabel,
  saveA11yHint,
  cancelA11yHint,
  style,
}) => {
  const insets = useSafeAreaInsets();
  const isKeyboardVisible = useKeyboardVisible();

  const paddingBottom = useMemo(() => {
    const basePadding = Platform.OS === 'android' ? 10 : 24;

    if (isKeyboardVisible) {
      return 8;
    }

    return Math.max(insets.bottom, basePadding);
  }, [insets.bottom, isKeyboardVisible]);

  const saveText = saveLabel ?? tt('save', 'Save');
  const cancelText = cancelLabel ?? tt('cancel', 'Cancel');

  const isSaveDisabled = isSaving || disableSave;
  const isCancelDisabled = isSaving;

  const shouldShowSave = showSave && !!onSave;
  const shouldShowCancel = showCancel && !!onCancel;

  if (!shouldShowSave && !shouldShowCancel) {
    return null;
  }

  return (
    <View
      testID={testID}
      accessibilityRole="toolbar"
      accessibilityLabel={footerA11yLabel ?? t('actions', 'Actions')}
      accessibilityState={{busy: isSaving}}
      style={[
        styles.footer,
        isKeyboardVisible && styles.footerCompact,
        {paddingBottom},
        style,
      ]}>
      {shouldShowCancel && (
        <View style={styles.cancelButton}>
          <KeyboardFocusable
            style={styles.fullWidth}
            testID={cancelTestID}
            onPress={isCancelDisabled ? undefined : onCancel}
            disabled={isCancelDisabled}
            accessible
            focusable
            accessibilityRole="button"
            accessibilityLabel={
              isCancelDisabled
                ? `${String(cancelText)}, disabled`
                : String(cancelText)
            }
            accessibilityHint={
              isCancelDisabled
                ? tt(
                    'cancelDisabledHint',
                    'Please wait while changes are being saved',
                  )
                : cancelA11yHint ?? t('cancelHint', 'Cancels your changes')
            }
            accessibilityState={{
              disabled: isCancelDisabled,
              busy: isSaving,
            }}>
            <Text
              allowFontScaling
              maxFontSizeMultiplier={2}
              style={[
                styles.cancelText,
                isCancelDisabled && styles.disabledText,
              ]}
              isBold>
              {cancelText}
            </Text>
          </KeyboardFocusable>
        </View>
      )}

      {shouldShowSave && (
        <View
          style={[
            styles.saveButton,
            !shouldShowCancel && styles.fullWidthSaveButton,
            isSaveDisabled && styles.disabledBtn,
          ]}>
          <KeyboardFocusable
            style={styles.fullWidth}
            testID={saveTestID}
            onPress={isSaveDisabled ? undefined : onSave}
            disableFocusRing={Platform.OS === 'web'}
            disabled={isSaveDisabled}
            accessible
            focusable
            accessibilityRole="button"
            accessibilityLabel={
              isSaveDisabled
                ? `${String(saveText)}, disabled`
                : String(saveText)
            }
            accessibilityHint={
              saveA11yHint ?? t('saveHint', 'Saves your changes')
            }
            accessibilityState={{
              disabled: isSaveDisabled,
              busy: isSaving,
            }}>
            {isSaving ? (
              <ActivityIndicator
                testID={ManageMySiteTestIDs.footer.savingIndicator}
                size="small"
                color="#333"
                accessibilityRole="progressbar"
                accessibilityLabel={t('saving', 'Saving')}
              />
            ) : (
              <Text
                allowFontScaling
                maxFontSizeMultiplier={2}
                style={[
                  styles.saveText,
                  isSaveDisabled && styles.disabledText,
                ]}>
                {saveText}
              </Text>
            )}
          </KeyboardFocusable>
        </View>
      )}
    </View>
  );
};

export default StickyActionFooter;
